import { Injectable } from '@angular/core'
import * as PIXI from 'pixi.js'
import { Dog } from 'src/domain/dog'
import {
  DOG_PROFILE_IMG_X,
  DOG_PROFILE_IMG_Y,
  EXPIRY_FONT_STYLE,
  ID_CARD_EXPIRY_TEXT_X,
  ID_CARD_EXPIRY_TEXT_Y,
  ID_CARD_NUMBER_FONT_STYLE,
  ID_CARD_NUMBER_X,
  ID_CARD_NUMBER_Y,
  IDCARD_META,
  USER_DATA_FONT_STYLE,
  USER_DATA_X_POS,
  USER_DATA_Y_DELTA,
  USER_DATA_Y_POS,
  USER_NAME_FONT_STYLE,
} from './idcard-render.constants'

export interface RenderCardParams {
  idCardType: keyof typeof IDCARD_META
  dog: Dog
  trainerName: string
  idNumber: string
  expiryText: string
  photoUrl: string
}

/**
 * Headlessly composites an idcard image (background + dog photo + text) without mounting a
 * canvas to the page - used for one-click renewal from the documents view, where there's no
 * interactive idcard.component instance to render into.
 */
@Injectable({ providedIn: 'root' })
export class IdCardRenderService {
  async renderCardBlob(params: RenderCardParams): Promise<Blob> {
    const meta = IDCARD_META[params.idCardType]
    const app = new PIXI.Application()
    try {
      await app.init({ width: meta.size[0], height: meta.size[1], backgroundColor: 0xffffff })

      const backgroundTexture = await PIXI.Assets.load(meta.backgroundUrl)
      app.stage.addChild(PIXI.Sprite.from(backgroundTexture))

      const photoTexture = await PIXI.Assets.load({ src: params.photoUrl, loadParser: 'loadTextures' })
      const photoSprite = PIXI.Sprite.from(photoTexture)
      photoSprite.anchor.set(0.5)
      photoSprite.x = DOG_PROFILE_IMG_X
      photoSprite.y = DOG_PROFILE_IMG_Y
      app.stage.addChild(photoSprite)

      const trainerNameText = new PIXI.Text({ text: params.trainerName, style: USER_NAME_FONT_STYLE })
      trainerNameText.x = USER_DATA_X_POS
      trainerNameText.y = USER_DATA_Y_POS
      app.stage.addChild(trainerNameText)

      const idNumberText = new PIXI.Text({ text: params.idNumber.toUpperCase(), style: ID_CARD_NUMBER_FONT_STYLE })
      idNumberText.anchor.set(1, 1)
      idNumberText.x = ID_CARD_NUMBER_X
      idNumberText.y = ID_CARD_NUMBER_Y
      app.stage.addChild(idNumberText)

      const expiryText = new PIXI.Text({ text: params.expiryText, style: EXPIRY_FONT_STYLE })
      expiryText.x = ID_CARD_EXPIRY_TEXT_X
      expiryText.y = ID_CARD_EXPIRY_TEXT_Y
      app.stage.addChild(expiryText)

      const baseY = USER_DATA_Y_POS + USER_DATA_Y_DELTA

      const dogName = new PIXI.Text({ text: params.dog.name, style: USER_NAME_FONT_STYLE })
      dogName.x = USER_DATA_X_POS
      dogName.y = baseY
      app.stage.addChild(dogName)

      const breed = new PIXI.Text({ text: this.capitalize(params.dog.breed), style: USER_DATA_FONT_STYLE })
      breed.x = USER_DATA_X_POS
      breed.y = baseY + 1 * USER_DATA_Y_DELTA
      app.stage.addChild(breed)

      const chipNumber = new PIXI.Text({ text: params.dog.chipNumber, style: USER_DATA_FONT_STYLE })
      chipNumber.x = USER_DATA_X_POS
      chipNumber.y = baseY + 2 * USER_DATA_Y_DELTA
      app.stage.addChild(chipNumber)

      return await new Promise<Blob>((resolve) => {
        app.renderer.extract.canvas(app.stage).toBlob((b: Blob) => resolve(b))
      })
    } finally {
      app.destroy(true)
    }
  }

  private capitalize(str: string) {
    if (str) {
      const firstLetter = str.charAt(0).toUpperCase()
      return str.length > 1 ? firstLetter + str.slice(1) : firstLetter
    }
    return undefined
  }
}
